/**
 * Entry point.
 *
 * Fixed 100 Hz loop -> simulation in the original's Z-up frame -> temporal smoothing ->
 * conversion to the render frame -> the deferred pipeline.
 */

import { Euler, Group, Mesh, Object3D, PerspectiveCamera, Vector3 } from 'three';
import { AssetStore } from './assets/loader';
import { hasEarlyAudioGesture, MatchAudio } from './audio';
import { BALL, RENDER_LAG_MS } from './core/constants';
import { simToRender } from './core/coords';
import { FixedLoop } from './core/loop';
import { mixSeed, seededRandom } from './core/random';
import { Vector3Smoother } from './core/smoother';
import { learningMode } from './input/learning';
import { PlayerInput } from './input/player';
import { MatchCamera } from './render/camera';
import { DeferredPipeline, type DirectionalLight } from './render/deferred';
import { floodlights } from './render/floodlights';
import { toDrawItems } from './render/geometry';
import { MARKER_LAYER, PlayerMarker } from './render/marker';
import { HumanoidFactory, DEFAULT_PLAYER_HEIGHT, type Humanoid, type PlayerAppearance } from './render/humanoid';
import { GoalNetting, NETTING_TEXTURE } from './render/netting';
import { ResetGlide } from './render/resetGlide';
import { Stage } from './render/stage';
import { annotateTouchBodyParts } from './sim/anim/bodypart';
import { loadLibrary, type AnimBundleIndex, type AnimClip } from './sim/anim/clip';
import { HumanoidAnim } from './sim/anim/runtime';
import type { PlayerPhysics } from './sim/anim/physics';
import { SPRINT_VELOCITY } from './sim/anim/velocity';
import { collisionBodyFromSkeleton } from './sim/collision';
import { Ball } from './sim/ball';
import { Match, MatchPlayer, TeamState } from './sim/match';
import {
  createOfficial,
  LINESMAN_A_START_POSITION,
  LINESMAN_B_START_POSITION,
  OFFICIAL_KIT_TEXTURE,
  REFEREE_START_POSITION,
  type Official,
} from './sim/officials';
import { stepMatch, type SteppedPlayer } from './sim/steploop';
import { FidelityTrace, TRACE_DEFAULT_MAX_TICKS, type TraceRosterEntry } from './sim/trace';
import { facingTowards, placeKickOff, type Side } from './sim/formation';
import {
  buildLineup,
  defaultFixture,
  outfieldKitTexture,
  type DatabaseFile,
  type DatabaseTeam,
  type TeamLineup,
} from './sim/lineup';
import { defaultSun, makeSun } from './sim/sun';
import { MatchStatsTracker } from './ui/scoreboard';
import {
  GameShell,
  type CameraSettings,
  type MarkerMode,
  type ShellFixture,
  type ShellMatchSession,
} from './ui/shell';

/**
 * The page, the GPU and the speakers: the drawing surface, the WebGL context, the corner
 * readout and the sound engine.
 */
interface GameScreen {
  canvas: HTMLCanvasElement;
  stage: Stage;
  pipeline: DeferredPipeline;
  hud: HTMLPreElement;
  audio: MatchAudio;
}

let gameScreen: GameScreen | null = null;

/**
 * The assets, opened once for the life of the page rather than once per match.
 *
 * A match can now be left and another one started without reloading, and the store is where
 * every decoded mesh and texture is cached. Opening a second one would fetch and decode the
 * whole ground again, and would leave the first one's textures alive with nothing pointing at
 * them; sharing it makes a second match cost the geometry and the players, and nothing else.
 */
let assetStore: Promise<AssetStore> | null = null;

function openAssets(): Promise<AssetStore> {
  assetStore ??= AssetStore.open('/assets');
  return assetStore;
}

/**
 * Let go of what one match built, so the next one can build its own.
 *
 * Only the geometry and the materials, never the textures: a texture belongs to the asset
 * store, is shared between matches and is handed straight back to the next one. The players'
 * meshes are the ones that matter — each of the twenty-two owns a private, skinned copy of the
 * body, and `Humanoid.dispose` is what releases it. Everything the ground is made of comes
 * from the store's cache and its geometry is deliberately left alone: `toDrawItems` hands back
 * the cached glTF's own buffers, so disposing them would empty the stadium for good.
 *
 * One thing `Humanoid.dispose` does that is worth knowing rather than working around: a
 * hairstyle mesh is NOT a private copy, so releasing one player's hair releases the graphics
 * buffers behind every player wearing the same style. Three.js simply uploads them again the
 * next time something draws them, which costs one frame at the start of the next match and
 * nothing else, so it is not worth a special case.
 *
 * Takes anything with a `humanoid` on it, not just `PlacedHumanoid[]`, so the three officials
 * (`PlacedOfficial`, which has everything `PlacedHumanoid` has and more) can be released
 * alongside the twenty-two players in one call.
 */
function releaseMatchScene(players: { humanoid: Humanoid }[]): void {
  for (const entry of players) entry.humanoid.dispose();
  for (const child of [...world.children]) {
    if (players.some((entry) => entry.humanoid.object === child)) continue;
    releaseMaterials(child);
  }
  world.clear();
}

function releaseMaterials(node: Object3D): void {
  node.traverse((child) => {
    const material = (child as Partial<Mesh>).material;
    if (Array.isArray(material)) for (const entry of material) entry.dispose();
    else material?.dispose();
  });
}

/**
 * Take hold of the page, once, the first time a match needs it.
 *
 * Deliberately not done while this module loads. Everything above the boot at the foot of the
 * file is arithmetic and asset plumbing that a test runner can exercise directly, and a test
 * runner has neither a document nor a graphics card; creating the WebGL context at load time
 * made the whole file impossible to import.
 */
function openScreen(): GameScreen {
  if (gameScreen) return gameScreen;

  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const stage = new Stage(canvas);

  // Sits below the shell's scoreboard, which occupies the top-left corner from 2.2% down
  // through a 38px-tall bar. Overlapping it made both unreadable.
  const hud = document.createElement('pre');
  hud.style.cssText =
    'position:fixed;top:64px;left:8px;margin:0;color:#9fe89f;font:12px ui-monospace,monospace;' +
    'text-shadow:0 1px 2px #000;pointer-events:none;line-height:1.5;white-space:pre';
  hud.hidden = true;
  document.body.appendChild(hud);

  // Created suspended: a browser will not play anything until the page has been interacted
  // with, so this installs its own one-shot gesture listener and resumes itself. But a match
  // can start well after that gesture already happened (the shell's own boot gate captures one
  // before the menu even shows), and a gamepad-only session may never fire another trusted
  // event again — so ask directly rather than only ever waiting for a fresh one.
  const audio = MatchAudio.init();
  if (hasEarlyAudioGesture()) void audio.resume();

  gameScreen = { canvas, stage, pipeline: new DeferredPipeline(stage.renderer), hud, audio };
  return gameScreen;
}

function status(text: string): void {
  if (gameScreen) gameScreen.hud.textContent = text;
}

// ---------------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------------

/**
 * The one place the axis change happens.
 *
 * The converted meshes, the animations and every gameplay coordinate are all in the
 * original's Z-up frame, because the converter deliberately applied no axis conversion. So
 * the whole world hangs off a single node carrying the rotation into three.js's Y-up frame,
 * and everything under it can be positioned in plain simulation metres. See core/coords.ts.
 */
const world = new Group();
world.rotation.copy(new Euler(-Math.PI / 2, 0, 0));

/**
 * Swap the advertising hoardings' placeholder artwork for real adverts.
 *
 * The stadium model paints all its boards with `media/textures/stadium/ad_placeholder.jpg`,
 * an 8x8 flat orange image. `Match::RandomizeAdboards` (`match.cpp:526-585`) replaces it at
 * match start: it scans `media/textures/adboards`, and for every material whose texture is
 * named `ad_placeholder…` it substitutes one of the sixteen adverts at random and sets
 * specular amount 0.2 and shininess 0.1. The name it tests is the bare file name rather than
 * the path, because a texture is registered under `get_file_name()` of what was asked for
 * (`resourcemanager.hpp:47`, `utils.cpp:187`).
 *
 * A browser cannot list a directory, so the generated manifest carries the list instead.
 *
 * Two differences from the original, both here rather than hidden:
 *  - Every board gets the SAME advert, where the original varies them. The asset converter
 *    merges the stadium by material, so all the boards arrive as one primitive with one
 *    texture; giving each its own advert needs the converter to keep the twenty-three source
 *    objects of that material apart, not a change here.
 *  - The draw is made from the run's seed rather than the process-wide generator, so a given
 *    seed always dresses the ground the same way.
 */
export function chooseAdboard(
  albedo: string | undefined,
  store: AssetStore,
  random: () => number,
): string | null {
  if (albedo === undefined) return null;
  const name = albedo.slice(albedo.lastIndexOf('/') + 1);
  if (!name.startsWith('ad_placeholder')) return null;

  const boards = store.manifest.adboards;
  if (boards.length === 0) return null;
  // `int(floor(random(0, size - 1.001f)))` — the original's own index. With sixteen files
  // that is 0 to 14, so the last advert is never drawn; kept as it stands, because the
  // alternative is inventing a different distribution for no visible gain.
  return boards[Math.min(Math.floor(random() * (boards.length - 1.001)), boards.length - 1)] ?? null;
}

interface StadiumBuild {
  drawn: number;
  /** Null only if the netting primitive could not be found — see the warning below. */
  netting: GoalNetting | null;
}

async function buildStadium(store: AssetStore, seed: number): Promise<StadiumBuild> {
  let drawn = 0;
  let netting: GoalNetting | null = null;
  // Its own stream, so dressing the ground never shifts what any player does.
  const adboardRandom = seededRandom(mixSeed(seed, 0xadb0));

  for (const key of [
    'media/objects/stadiums/test/test.ase',
    'media/objects/stadiums/test/pitch.ase',
    // The posts, crossbars and netting are a separate model, loaded separately by the
    // original too (`match.cpp:212`). The netting primitive within it is now deformable —
    // see render/netting.ts — identified below by its own material rather than by anything
    // special about this loop.
    'media/objects/stadiums/goals.ase',
  ]) {
    const gltf = await store.mesh(key);
    for (const item of toDrawItems(gltf)) {
      const advert = chooseAdboard(item.material.albedo, store, adboardRandom);
      if (advert !== null) {
        item.material.albedo = advert;
        item.material.specularAmount = 0.2;
        item.material.shininess = 0.1;
      }

      // Only the albedo is colour; the rest are data. See AssetStore.texture().
      const textures = {
        albedo: item.material.albedo ? await store.texture(item.material.albedo, true) : null,
        normal: item.material.normal ? await store.texture(item.material.normal, false) : null,
        specular: item.material.specular
          ? await store.texture(item.material.specular, false)
          : null,
        illumination: item.material.illumination
          ? await store.texture(item.material.illumination, false)
          : null,
      };
      const mesh = new Mesh(item.geometry, DeferredPipeline.createGeometryMaterial(item, textures));
      mesh.frustumCulled = false; // merged by material, so bounds span the whole stadium
      world.add(mesh);
      drawn++;

      // GoalNetting replaces this item's position attribute with its own dynamic one — see
      // its module comment. `mesh` above only ever holds a reference to item.geometry, so it
      // picks up whatever attribute ends up attached regardless of this ordering.
      if (key === 'media/objects/stadiums/goals.ase' && item.material.albedo === NETTING_TEXTURE) {
        netting = new GoalNetting(item.geometry);
      }
    }
  }

  if (!netting) {
    console.warn('[gf] goal netting primitive not found; the net will not deform on a goal');
  }

  return { drawn, netting };
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

interface PlacedHumanoid {
  humanoid: Humanoid;
  anim: HumanoidAnim;
  player: MatchPlayer;
  lineup: TeamLineup;
  name: string;
  /** 0 to 10, his place in the starting eleven. The trace is ordered by it. */
  slot: number;
  /** His row in the shipped database, so a trace pair can be checked for the same lineup. */
  databaseId: number;
}

/**
 * Which sides currently have somebody driving them.
 *
 * The original never freezes this at kick-off. `Player::GetStat` (`player.cpp:532`) and
 * `PlayerController::GetReactionTime_ms` (`playercontroller.cpp:55`) both ask the team for its
 * human-gamer count on every single call, and `Match::UpdateControllerSetup` (`match.cpp:587`)
 * rebuilds those lists the moment the in-match device screen is closed
 * (`menu/controllerselect.cpp:129-132`). So moving a controller to the other side has to move
 * the handicap with it, and handing it back to the computer has to bring the handicap back.
 */
export class HumanSides {
  private readonly sides = new Set<number>();

  constructor(controllers: readonly { side: number }[]) {
    this.set(controllers);
  }

  set(controllers: readonly { side: number }[]): void {
    this.sides.clear();
    for (const controller of controllers) {
      if (controller.side !== 0) this.sides.add(controller.side);
    }
  }

  has(side: number): boolean {
    return this.sides.has(side);
  }
}

/**
 * How far below his real ability a player on a computer-run side reads every stat.
 *
 * `Player::GetStat` (`player.cpp:532`): 1 when somebody is driving this side, and
 * `0.3 + 0.7 * difficulty` when nobody is. Returned as a function, not a number, because the
 * original asks the question on every stat read and the answer changes the moment a device is
 * moved between sides.
 */
export function difficultyHandicap(
  humanSides: HumanSides,
  side: number,
  difficulty: number,
): () => number {
  return () => (humanSides.has(side) ? 1 : 0.3 + 0.7 * difficulty);
}

/**
 * The ±2 m sideways scatter that keeps players from starting on top of one another.
 *
 * `teamAIcontroller.cpp:701` draws it from the process-wide generator, which is seeded once
 * per run, so it has to come from this run's seed here. A fixed constant makes every seed
 * line the twenty outfield players up in exactly the same place, which defeats the point of
 * the match taking a seed at all.
 */
export function kickOffJitter(seed: number, teamId: number): () => number {
  return seededRandom(mixSeed(seed, teamId));
}

/**
 * Tell each player's movement solver how recently he played the ball.
 *
 * `powerFactor` (`humanoidbase.cpp:2082`) takes power out of a player for about half a second
 * after a touch and gives it back over the next half, and it is applied to the per-frame
 * velocity-change budget at `:2399`. The bias decays over a second (`playerbase.cpp:141-146`)
 * from the moment of contact (`team.cpp:245`).
 *
 * Done here rather than while the players are being built because the decay is measured
 * against the match clock, and the match cannot exist until all twenty-two of them do.
 */
export function wireLastTouchBias(players: readonly MatchPlayer[], match: Match): void {
  for (const player of players) {
    player.physics.lastTouchBias = (decayMs: number): number =>
      player.lastTouchBias(decayMs, match.timeMs);
  }
}

/**
 * What the animation solver needs to know about a player.
 *
 * Every stat read carries two multipliers, exactly as the original applies them
 * (`player.cpp:526-540`): the side-wide difficulty handicap, which applies only to a team with
 * nobody driving it, and tiredness, which the match updates each tick. So a spent player is
 * worse at everything at once — control, tackling, passing, agility — rather than merely
 * slower. `statMultiplier` is a function rather than a number because the first of those two
 * can change in the middle of a match; see HumanSides.
 *
 * An injury (`docs/design/violence-and-variety.md`, tranche 2.4) scales the same read a
 * second way, through `injuryFactorInv`: a knock takes something off every skill exactly as
 * tiredness does, so it multiplies into the SAME expression rather than opening a parallel
 * one. Absent — the ordinary case — it is 1 and changes nothing.
 */
export function playerPhysics(
  stats: Readonly<Record<string, number>>,
  statMultiplier: () => number,
): PlayerPhysics {
  const physics: PlayerPhysics = {
    // playerbase.cpp:131-139 — every player tops out between 7.2 and 8.0 m/s. Read through
    // the unmodified accessor, so neither difficulty nor tiredness moves it.
    maxVelocity: SPRINT_VELOCITY * (0.9 + (stats['physical_velocity'] ?? 0.5) * 0.1),
    fatigueFactorInv: 1,
    injuryFactorInv: 1,
    stat: (name: string): number =>
      (stats[name] ?? 0.5) *
      statMultiplier() *
      (0.7 + 0.3 * (physics.fatigueFactorInv ?? 1)) *
      (physics.injuryFactorInv ?? 1),
    // A placeholder, replaced by `wireLastTouchBias` the moment the match exists: the decay
    // is measured against the match clock, and there is no match yet at this point.
    lastTouchBias: () => 0,
    decayingPositionOffset: new Vector3(),
  };
  return physics;
}

async function buildTeams(
  store: AssetStore,
  library: AnimClip[],
  ball: Ball,
  fixture: ShellFixture,
  seed: number,
  humanSides: HumanSides,
): Promise<{
  placed: PlacedHumanoid[];
  match: Match;
  /** Put one or both sides into a different shirt. Built here, where the kit art is known. */
  wearKits: (homeKit: number, awayKit: number) => Promise<void>;
}> {
  const database = await store.database<DatabaseFile>();
  const fallback = defaultFixture(database);
  const findTeam = (id: number): DatabaseTeam | undefined =>
    database.teams.find((team) => team.id === id);
  const homeTeam = findTeam(fixture.homeTeamId) ?? fallback[0];
  const awayTeam = findTeam(fixture.awayTeamId) ?? fallback[1];
  const hasTexture = (key: string): boolean => {
    const entry = store.manifest.textures[key];
    return entry !== undefined && entry.missing !== true;
  };

  const lineups: TeamLineup[] = [
    buildLineup(database, homeTeam, 0, hasTexture),
    buildLineup(database, awayTeam, 1, hasTexture),
  ];

  const factory = await HumanoidFactory.create(store);

  // The ball sits on the centre spot at a kick-off, and everyone looks at it.
  const ballSpot = new Vector3(0, 0, 0);
  const placed: PlacedHumanoid[] = [];
  const squads: MatchPlayer[][] = [[], []];
  let nextId = 0;

  for (const lineup of lineups) {
    // Team 0 defends the negative-x goal (`Team::GetSide`, team.cpp:102-113).
    const side: Side = lineup.index === 0 ? -1 : 1;
    const spots = placeKickOff(lineup.formation, side, {
      jitter: kickOffJitter(seed, lineup.teamId),
      isTaker: lineup.index === 0,
    });

    for (const player of lineup.players) {
      const spot = spots[player.slot];
      if (!spot) continue;

      const humanoid = await factory.build(player.appearance);
      // Only so the very first drawn frame is sane; from then on the animation runtime
      // owns both the pose and the heading, and overwrites these every frame.
      humanoid.setFacing(facingTowards(spot.position, ballSpot));
      humanoid.setPosition(spot.position);
      humanoid.update();

      // The original picks the first idle frame at random. Deriving it from the player's
      // own database id instead keeps every screenshot and comparison identical, which
      // matters far more right now than the difference being genuinely random.
      const idleFrameBias = ((player.databaseId * 2654435761) % 1000) / 1000;

      const controllerSide = lineup.index === 0 ? -1 : 1;
      const physics = playerPhysics(
        player.stats,
        difficultyHandicap(humanSides, controllerSide, fixture.difficulty),
      );
      const anim = new HumanoidAnim({
        library,
        player: physics,
        position: spot.position,
        focus: ballSpot,
        height: player.appearance.height ?? 1.92,
        idleFrameBias,
        // His own stream of chance, for the scuff on a trap and the curl on a shot. Derived
        // from the run's seed and his database id, so the same seed replays the same match
        // and no two players share a sequence. Before this he was handed a function that
        // returned one constant for ever, which meant every shot he ever hit went wrong in
        // exactly the same way.
        random: seededRandom(mixSeed(seed, player.databaseId)),
      });

      // icontroller.cpp:14-16 — a sharp player notices things 40 ms sooner than a slow one,
      // plus playercontroller.cpp:55, which slows a side with nobody driving it further.
      //
      // KNOWN DIVERGENCE, and the one half of this the original recomputes that we cannot:
      // the original works this out inside `PlayerController::Process`, once per tick, so it
      // follows a controller across sides like the stat handicap above. Here it is a value
      // handed to the MatchPlayer, whose `reactionTimeMs` is readonly, so it stays as it was
      // at kick-off. Dropping `readonly` at `src/sim/match.ts:155` is all that is missing.
      const reactionTimeMs =
        Math.round(80 - physics.stat('physical_reaction') * 40) +
        (humanSides.has(controllerSide) ? 0 : (1 - fixture.difficulty) * 100);
      const matchPlayer = new MatchPlayer(nextId++, lineup.index, anim, physics, reactionTimeMs, {
        role: player.role,
        formationEntry: lineup.formation[player.slot]!,
        // Raw, not through the handicap: it sets how fast he tires, and running the drain
        // rate through the same multiplier that tiredness itself feeds would compound.
        stamina: player.stats['physical_stamina'] ?? 0.5,
        collisionBody: () =>
          collisionBodyFromSkeleton(
            humanoid.skeleton,
            anim.position,
            (player.appearance.height ?? 1.92) / 1.92,
          ),
      });
      squads[lineup.index]!.push(matchPlayer);

      world.add(humanoid.object);
      placed.push({
        humanoid,
        anim,
        player: matchPlayer,
        lineup,
        name: player.name,
        slot: player.slot,
        databaseId: player.databaseId,
      });
    }
  }

  const match = new Match({
    ball,
    teams: [
      new TeamState(0, -1, squads[0]!),
      new TeamState(1, 1, squads[1]!),
    ],
    matchDuration: fixture.duration,
    initialKickOff: true,
    difficulty: fixture.difficulty,
    violence: fixture.violence,
    seed,
  });

  wireLastTouchBias(
    placed.map((entry) => entry.player),
    match,
  );

  /**
   * The goalkeeper is left alone: both sides share the one keeper shirt that ships with the
   * game, so there is no second one to put him in (`team.cpp:91`).
   */
  const wearKits = async (homeKit: number, awayKit: number): Promise<void> => {
    for (const [index, team, kit] of [
      [0, homeTeam, homeKit],
      [1, awayTeam, awayKit],
    ] as const) {
      const texture = await store.texture(outfieldKitTexture(team, index, kit, hasTexture), true);
      for (const entry of placed) {
        if (entry.lineup.index !== index || entry.player.role === 'GK') continue;
        entry.humanoid.setKitTexture(texture);
      }
    }
  };

  return { placed, match, wearKits };
}

// ---------------------------------------------------------------------------
// Officials
// ---------------------------------------------------------------------------

/** One official's render half, alongside the simulation half `createOfficial` builds. */
interface PlacedOfficial extends Official {
  humanoid: Humanoid;
}

/**
 * Build the referee and the two linesmen — port of `officials.cpp:20-54`.
 *
 * Each is built exactly like one of the twenty-two players, through the same factory and the
 * same clip library, wearing the referee's own kit (`officials.cpp:41,145-147`) and standing
 * where the original starts them. Nothing here or afterward ever moves them at a restart,
 * matching the original, which never repositions an official either (see `src/sim/officials.ts`
 * for the full port and for why they are never added to a `TeamState`, and so never touch
 * collision or the fidelity trace).
 */
async function buildOfficials(
  store: AssetStore,
  library: AnimClip[],
  seed: number,
): Promise<PlacedOfficial[]> {
  // A private factory rather than sharing buildTeams's: the skeleton, rest poses, skin data and
  // body mesh it reads are all cached by AssetStore (assets/loader.ts), so this costs one extra
  // bind-pose bake, not one extra network fetch.
  const factory = await HumanoidFactory.create(store);
  const appearance: PlayerAppearance = {
    height: DEFAULT_PLAYER_HEIGHT,
    skinColor: 1,
    hairStyle: 'bald',
    hairColor: 'black',
    kitTexture: OFFICIAL_KIT_TEXTURE,
  };
  const centreSpot = new Vector3(0, 0, 0);

  const specs: { kind: Official['kind']; position: Vector3; salt: number }[] = [
    { kind: 'referee', position: REFEREE_START_POSITION, salt: 1 },
    { kind: 'linesman', position: LINESMAN_A_START_POSITION, salt: 2 },
    { kind: 'linesman', position: LINESMAN_B_START_POSITION, salt: 3 },
  ];

  const placed: PlacedOfficial[] = [];
  for (const spec of specs) {
    const humanoid = await factory.build(appearance);
    // Only so the very first drawn frame is sane, exactly as for a player above; the
    // animation runtime owns both the pose and the heading from the first tick onward.
    humanoid.setFacing(facingTowards(spec.position, centreSpot));
    humanoid.setPosition(spec.position);
    humanoid.update();
    world.add(humanoid.object);

    const official = createOfficial(spec.kind, {
      // Comfortably clear of the 22 players' own ids (0..21).
      id: 1000 + spec.salt,
      library,
      position: spec.position,
      skeleton: humanoid.skeleton,
      random: seededRandom(mixSeed(seed, 0xf0 + spec.salt)),
      // The same trick as a player's idle-frame bias below: derived rather than drawn, so a
      // screenshot or a trace comparison never depends on build order.
      idleFrameBias: ((spec.salt * 2654435761) % 1000) / 1000,
    });
    placed.push({ ...official, humanoid });
  }

  return placed;
}

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------

const ball = new Ball(new Vector3(0, 0, BALL.radius));
const ballSmoother = new Vector3Smoother();
let ballMesh: Mesh | null = null;
let netting: GoalNetting | null = null;

/**
 * The sun, from the original's own parameters — see sim/sun.ts.
 *
 * The original's light type is always directional; its point-light branch is unreachable
 * (docs/original-engine/renderer.md §2.3). The sun is defined in the simulation's Z-up
 * frame, so it is converted to the render frame here like everything else.
 *
 * defaultSun() rather than makeSun() for now: the original randomises the sun per match,
 * which is right for play but makes every screenshot and comparison different. Switch to
 * makeSun() when matches start.
 *
 * The four floodlight gantries follow it — PENDING.md "The floodlights are unlit geometry."
 * They use the same `DirectionalLight` entries as the sun, just close and finite-radius
 * instead of far and huge, which is what turns the shared shader's point-light machinery
 * into a local pool of light instead of a uniform one. See render/floodlights.ts for the
 * measurement behind their positions and why nothing new had to be built for this.
 */
const sun = defaultSun();
export const lights: DirectionalLight[] = [
  {
    position: simToRender(sun.position),
    color: sun.color,
    radius: sun.radius,
  },
  ...floodlights(),
];

/**
 * Where the visual-options page's "randomize sun position" draws from.
 *
 * A stream rather than the platform's own generator, and one shared by the whole page rather
 * than made afresh per match, so the first press of that button always gives the same sun, the
 * second always gives the same second one, and a run can be repeated.
 */
const sunStream = seededRandom(0x5c1f3ea7);

/** A recording the address bar asked for. */
interface TraceRequest {
  runId: string;
  inputId: string | null;
  sourceRevision: string | null;
  maxTicks: number;
  seed: number;
}

/**
 * Read what the address bar asked for.
 *
 * A recording is switched on from the address bar because that is the one place a script
 * driving the page can reach before a match exists. `?trace=friendly-001` records under that
 * name and `?trace=1` records under a default one; without the parameter the match carries no
 * recorder at all. `seed` picks which match gets played, whether or not anything is being
 * recorded, and is written into the recording's header. `traceTicks=0` records without limit
 * and any other number caps it — see TRACE_DEFAULT_MAX_TICKS for why the default stops where
 * it does. `traceInput` and `traceRev` fill in the two header fields only the caller knows.
 */
export function readTraceRequest(search: string): { seed: number; trace: TraceRequest | null } {
  const params = new URLSearchParams(search);
  const seed = Number.parseInt(params.get('seed') ?? '', 10);
  const safeSeed = Number.isFinite(seed) ? seed >>> 0 : 0;

  const requested = params.get('trace');
  if (requested === null || requested === '' || requested === '0') {
    return { seed: safeSeed, trace: null };
  }

  const ticks = Number.parseInt(params.get('traceTicks') ?? '', 10);
  return {
    seed: safeSeed,
    trace: {
      runId: requested === '1' ? 'web-run' : requested,
      inputId: params.get('traceInput'),
      sourceRevision: params.get('traceRev'),
      maxTicks: Number.isFinite(ticks) && ticks >= 0 ? ticks : TRACE_DEFAULT_MAX_TICKS,
      seed: safeSeed,
    },
  };
}

/**
 * Start recording, and decide how the bytes get out of the browser.
 *
 * A page cannot write a file, so a finished recording is offered as a download and is also
 * left on `window.__gfTrace`, where anything driving the page can read the whole thing out of
 * `toText()` without a download dialog ever appearing. Both paths carry the same bytes.
 */
function attachTrace(
  match: Match,
  players: PlacedHumanoid[],
  request: TraceRequest,
): FidelityTrace | null {
  const roster: TraceRosterEntry[] = [];
  for (const team of [0, 1] as const) {
    const squad = players
      .filter((entry) => entry.lineup.index === team)
      .sort((a, b) => a.slot - b.slot);
    for (const entry of squad) {
      roster.push({ team, slot: entry.slot, playerDbId: entry.databaseId, player: entry.player });
    }
  }

  const home = players.find((entry) => entry.lineup.index === 0)?.lineup;
  const away = players.find((entry) => entry.lineup.index === 1)?.lineup;
  if (!home || !away) return null;

  try {
    const trace = new FidelityTrace({
      run: {
        id: request.runId,
        inputId: request.inputId,
        rngSeed: request.seed,
        sourceRevision: request.sourceRevision,
      },
      teams: [
        { teamDbId: home.teamId, name: home.name },
        { teamDbId: away.teamId, name: away.name },
      ],
      roster,
      maxTicks: request.maxTicks,
      onComplete: (finished) => {
        console.info(`[gf] fidelity trace complete: ${finished.tickCount} ticks`);
        finished.download(`${request.runId}.gftrace`);
      },
    });
    match.trace = trace;
    console.info(
      `[gf] recording a fidelity trace as "${request.runId}", seed ${request.seed}, ` +
        `${request.maxTicks === 0 ? 'no tick limit' : `${request.maxTicks} ticks`}`,
    );
    return trace;
  } catch (error) {
    console.error('[gf] could not start the fidelity trace', error);
    return null;
  }
}

async function startMatch(fixture: ShellFixture): Promise<ShellMatchSession> {
  const { canvas, stage, pipeline, hud, audio: matchAudio } = openScreen();
  status('loading assets…');
  hud.hidden = false;

  // Read before anything is built: the seed decides both which match gets played and how the
  // ground is dressed, and the hoardings go up before the teams do.
  const { seed, trace: traceRequest } = readTraceRequest(window.location.search);

  const store = await openAssets();
  await matchAudio.load(store);
  // A second match in the same session starts from a clean crowd and a clean scoreline
  // rather than from the last one's roar.
  matchAudio.reset();
  const { drawn: stadiumDraws, netting: builtNetting } = await buildStadium(store, seed);
  netting = builtNetting;

  // The ball, from the original's own model.
  try {
    const ballGltf = await store.mesh('media/objects/balls/generic.ase');
    for (const item of toDrawItems(ballGltf)) {
      const albedo = item.material.albedo ? await store.texture(item.material.albedo, true) : null;
      ballMesh = new Mesh(
        item.geometry,
        DeferredPipeline.createGeometryMaterial(item, {
          albedo,
          normal: null,
          specular: null,
          illumination: null,
        }),
      );
      ballMesh.frustumCulled = false;
      world.add(ballMesh);
    }
  } catch (error) {
    console.warn('[gf] ball model failed to load', error);
  }

  status('loading animations…');
  const [animationIndex, animationBuffer] = await Promise.all([
    store.animationIndex<AnimBundleIndex>(),
    store.animationData(),
  ]);
  const library = loadLibrary(animationIndex, animationBuffer);

  status('building teams…');
  const humanSides = new HumanSides(fixture.controllers);
  const { placed: players, match, wearKits } = await buildTeams(
    store,
    library,
    ball,
    fixture,
    seed,
    humanSides,
  );
  // The referee and the two linesmen. Built after the match exists so their positions could
  // read from it if they ever needed to, though today they do not: see buildOfficials.
  const officials = await buildOfficials(store, library, seed);
  const officialSmoothers = officials.map(() => new Vector3Smoother());
  // Seed the tracker with the opening state, so the first tick compares against a real
  // snapshot rather than firing every sound at once.
  matchAudio.update(match);
  // Possession, shots and corners for the full-time screen. The simulation counts none of
  // them, so they are watched from out here, one tick at a time — see ui/scoreboard.ts.
  const matchStats = new MatchStatsTracker();
  const input = new PlayerInput(fixture.controllers);
  // Which of the twenty-two somebody is actually driving. The simulation needs this in two
  // places — whether to leave the errand of chasing the ball with the man who has it
  // (`team.cpp:419-420`), and it will need it again for the rules that switch the computer's
  // sliding tackle off on a side a person is playing on — but nothing below this file knows,
  // because it is the input layer that owns the mapping. Answering "nobody" is right for a
  // match nobody is playing, which is what it falls back to.
  match.humanControlled = (player) =>
    input
      .controllerIds()
      .some((deviceId) => input.controlledPlayer(deviceId, match) === player);
  // Which body part meets the ball is not in the clip files: it comes from posing a
  // skeleton at the contact frame. Any player's skeleton will do — they are all the same
  // rig — so borrow the first one.
  const touchClips = annotateTouchBodyParts(library, players[0]!.humanoid.skeleton);
  const smoothers = players.map(() => new Vector3Smoother());
  // A restart teleport clears the smoothers below; this is what bridges the cut so a player is
  // never drawn cutting instantly from where the stoppage held him to his new mark. See
  // `render/resetGlide.ts`.
  const resetGlides = players.map(() => new ResetGlide());
  /** The same twenty-two, in the shape one simulation tick wants them. */
  const stepped: SteppedPlayer[] = players.map((entry) => ({
    player: entry.player,
    anim: entry.anim,
    skeleton: entry.humanoid.skeleton,
  }));

  pipeline.setGeometry([world]);

  const camera = stage.camera as PerspectiveCamera;
  // Markers live on their own layer so the shadow pass skips them; the scene camera has to
  // be told to look at that layer as well as the default one.
  camera.layers.enable(MARKER_LAYER);
  // The shake draws from the run's own seed, so the same match is filmed identically twice over
  // and a change to the picture can be told apart from noise.
  const matchCamera = new MatchCamera(seededRandom(mixSeed(seed, 0xca3e)));
  // One aim point before the first frame, so the opening render has a pose to interpolate
  // rather than the placeholder the constructor left behind.
  matchCamera.tick(match, 0);

  // Cyan for the man you are driving, amber for whoever is on the ball when that is somebody
  // else. Two colours rather than one, because "who am I" and "where is the ball" are
  // different questions and a defender needs both answered at once.
  // Built on demand rather than up front, so a controller handed a side in the middle of a
  // match gets a ring too.
  const controlMarkers = new Map<string, PlayerMarker>();
  const controlMarker = (controllerId: string): PlayerMarker => {
    let marker = controlMarkers.get(controllerId);
    if (!marker) {
      marker = new PlayerMarker('#5ce1e6');
      controlMarkers.set(controllerId, marker);
      world.add(marker.mesh);
    }
    return marker;
  };
  const possessionMarker = new PlayerMarker('#ffc247');
  world.add(possessionMarker.mesh);
  let markerMode: MarkerMode = 'all';

  let paused = false;

  const loop = new FixedLoop({
    tick(simTimeMs) {
      if (paused) {
        // Still read the devices, or every key touched while the pause menu is up is banked and
        // delivered in one lump on the first tick after play resumes.
        input.drain();
        return;
      }
      const resetSerial = match.resetSerial;
      // The tick itself is shared with the measurement bench, so the game a person plays and
      // the game that gets measured are the same game. See sim/steploop.ts.
      stepMatch({
        match,
        ball,
        players: stepped,
        // The referee and the two linesmen walk as the last act of the tick. They used to have
        // their own pass out here, which was fine while they were in none of the football; now
        // that the referee's judgment answers to where he is standing, the bench has to walk
        // him too, so the one loop both of them call does it.
        officials,
        commands: (player) => input.commands(match, player),
        afterStep: () => {
          if (match.resetSerial !== resetSerial) {
            // The teleport has already happened in `players[i].anim`, and the smoothers below
            // still hold nothing but where everyone stood through the stoppage — exactly the
            // two ends a glide needs, captured before `clear()` throws the old end away.
            for (let i = 0; i < players.length; i++) {
              if (!players[i]!.player.active) continue;
              const target = players[i]!.anim.worldPosition();
              const held = smoothers[i]!.sampleCount > 0
                ? smoothers[i]!.get(simTimeMs, RENDER_LAG_MS)
                : target;
              resetGlides[i]!.begin(held, target, simTimeMs);
            }
            ballSmoother.clear();
            for (const smoother of smoothers) smoother.clear();
          }
          input.tick(match);
        },
      });
      // A dismissed man stays visible and his smoother keeps tracking him for a bounded window
      // — `isDeparting` — so he is seen walking for the touchline rather than cut instantly.
      for (const entry of players) {
        entry.humanoid.object.visible = entry.player.active || entry.player.isDeparting(match.timeMs);
      }
      ballSmoother.set(ball.position, simTimeMs);
      matchAudio.update(match);
      // Every figure on the full-time screen is a change between two ticks, so this has to
      // see all of them: a skipped tick loses whatever happened on it, silently.
      matchStats.sample(match);
      for (let i = 0; i < players.length; i++) {
        const entryPlayer = players[i]!.player;
        if (!entryPlayer.active && !entryPlayer.isDeparting(match.timeMs)) continue;
        smoothers[i]!.set(players[i]!.anim.worldPosition(), simTimeMs);
      }
      // They walked inside stepMatch above; all that is left out here is the render
      // smoothing, mirroring the players' loop.
      for (let i = 0; i < officials.length; i++) {
        officialSmoothers[i]!.set(officials[i]!.anim.worldPosition(), simTimeMs);
      }
      // Last, so the camera aims at the state the tick just produced.
      matchCamera.tick(match, simTimeMs);
    },
    render(simTimeMs) {
      // Nobody a person is driving may move while play is stopped for a restart: the taker may
      // only play the ball and everyone else holds his position, and the clock does not run
      // either. Without a caption that is indistinguishable from a hung game. So the caption
      // goes up for any restart at all with a person on the pitch, and names the key only when
      // the restart is that person's own to take.
      const driven = match.inSetPiece
        ? input.controllerIds().map((id) => input.controlledPlayer(id, match))
        : [];
      const takerDriver = match.setPieceTaker ? input.driverOf(match.setPieceTaker) : null;
      shell?.updateHud(
        match,
        driven.some((player) => player !== null)
          ? { setPiece: match.setPiece, deviceKind: takerDriver?.kind ?? null }
          : null,
        // Learning mode: what the button just pressed did. Read every frame, and the shell
        // decides whether it is showing — the input layer writes the line either way, so
        // switching the mode on mid-match needs nothing to be rewired.
        learningMode() ? input.hint(match) : null,
      );
      if (ballMesh) {
        // Under `world`, so simulation metres go straight in — no per-object conversion.
        ballMesh.position.copy(ballSmoother.get(simTimeMs, RENDER_LAG_MS));
        // Same reasoning, same position: the net should bulge toward wherever the ball
        // sprite is actually drawn this frame, not toward its un-lagged simulation position
        // — see render/netting.ts.
        netting?.update(ballMesh.position, ball.touchesNet, simTimeMs);
      }

      // A glide in progress overrides the ordinary jitter smoother — see `resetGlide.ts` — so
      // the body and, below, its marker are drawn crossing the pitch rather than cutting to it.
      const playerRenderPosition = (i: number): Vector3 =>
        resetGlides[i]!.sample(simTimeMs) ?? smoothers[i]!.get(simTimeMs, RENDER_LAG_MS);

      for (let i = 0; i < players.length; i++) {
        const entry = players[i]!;
        // A dismissed man keeps being positioned and drawn for `PLAYER_WALK_OFF_MS` after
        // `Match.sendOff` — see `isDeparting` — so he is seen walking off rather than cut.
        if (!entry.player.active && !entry.player.isDeparting(match.timeMs)) {
          entry.humanoid.object.visible = false;
          continue;
        }
        // The pose was stepped at 100 Hz in tick(); only the pitch position is interpolated.
        entry.humanoid.setPosition(playerRenderPosition(i));
        entry.humanoid.update();
      }

      // The referee and the two linesmen, interpolated the same way; they are never
      // dismissed, so there is no visibility toggle to match the players' loop above.
      for (let i = 0; i < officials.length; i++) {
        const official = officials[i]!;
        official.humanoid.setPosition(officialSmoothers[i]!.get(simTimeMs, RENDER_LAG_MS));
        official.humanoid.update();
      }

      // Markers follow the same interpolated positions the bodies do, so a ring never drifts
      // off the man it belongs to.
      const marked = new Set<MatchPlayer>();
      const placeMarker = (marker: PlayerMarker, target: MatchPlayer | undefined): void => {
        const index = target ? players.findIndex((entry) => entry.player === target) : -1;
        if (markerMode === 'off' || index < 0 || !players[index]!.player.active) {
          marker.hide();
          return;
        }
        marked.add(players[index]!.player);
        marker.show(playerRenderPosition(index));
      };

      const driving = new Set(input.controllerIds());
      for (const controllerId of driving) {
        placeMarker(controlMarker(controllerId), input.controlledPlayer(controllerId, match) ?? undefined);
      }
      // A device that has left the match keeps its ring object but stops showing it.
      for (const [controllerId, marker] of controlMarkers) {
        if (!driving.has(controllerId)) marker.hide();
      }
      const onTheBall = match.designatedPossessionPlayer;
      // Only when he is not already ringed as somebody's own player — two rings stacked on
      // one man reads as a bug.
      placeMarker(
        possessionMarker,
        markerMode === 'all' && !marked.has(onTheBall) ? onTheBall : undefined,
      );

      matchCamera.apply(camera, simTimeMs);
      pipeline.setSize(canvas.width, canvas.height);
      pipeline.render(camera, lights);
    },
  });

  // The recorder, if the address bar asked for one. It attaches to the match, which samples
  // itself at the end of every tick; nothing in the loop above knows it exists.
  const trace = traceRequest ? attachTrace(match, players, traceRequest) : null;
  globalThis.__gfTrace = trace;

  loop.start();

  const c = stage.capabilities;
  const home = players.find((p) => p.lineup.index === 0)?.lineup;
  const away = players.find((p) => p.lineup.index === 1)?.lineup;
  // Each player is six material groups plus, usually, a hairstyle.
  const playerDraws = players.reduce((total, p) => total + p.humanoid.drawCount, 0);
  // The loading messages are gone and the pitch is about to be drawn. What is left in this
  // element is a developer's readout over somebody else's football match, and it covers a
  // quarter of the picture — so it goes away until VISUAL OPTIONS asks for it back.
  hud.hidden = true;
  const statusTimer = setInterval(() => {
    if (hud.hidden) return;
    status(
      [
        `fps            ${loop.stats.fps.toFixed(0)}`,
        `sim            ${(loop.stats.simTimeMs / 1000).toFixed(1)}s (${loop.stats.ticksLastFrame}/frame)`,
        `dropped        ${loop.stats.droppedTicks}`,
        `match          ${home?.name ?? '?'} v ${away?.name ?? '?'}`,
        `players        ${players.length}`,
        `on the ball    ${players.find((p) => p.player === match.designatedPossessionPlayer)?.name ?? '-'}`,
        `controlled     ${fixture.controllers
          .filter((controller) => controller.side !== 0)
          .map((controller) => {
            const selected = input.controlledPlayer(controller.id, match);
            const name = players.find((entry) => entry.player === selected)?.name ?? '-';
            return `${controller.label}: ${name}`;
          })
          .join(' · ') || '-'}`,
        `touch clips    ${touchClips}`,
        `draw calls     ${stadiumDraws + 1 + playerDraws} ` +
          `(${stadiumDraws} stadium, 1 ball, ${playerDraws} players)`,
        `assets         ${(store.manifest.totalBytes / 1e6).toFixed(2)} MB`,
        ``,
        `float buffers  ${c.colorBufferFloat}`,
        `draw buffers   ${c.maxDrawBuffers}`,
        `gpu            ${c.renderer}`,
      ].join('\n'),
    );
  }, 250);

  globalThis.__gf = {
    loop,
    stage,
    ball,
    match,
    pipeline,
    camera,
    store,
    lights,
    world,
    players,
    officials,
    input,
  };

  return {
    setPaused(value: boolean): void {
      paused = value;
      // The tick above returns before ever reaching matchAudio.update(match) while paused, so
      // nothing else would touch the crowd's gain nodes for as long as the menu is up.
      matchAudio.setPaused(value);
    },
    /**
     * Hand the page back to the menus.
     *
     * This used to reload the browser, which does work — nothing in this game is ever saved —
     * but it throws away the menus along with the match and lands the player back on the title
     * screen, several seconds of asset decoding later. It is now a real teardown: the loop
     * stops, the devices are let go, the scene is emptied and the crowd goes quiet. The assets
     * stay where they are, so the next match reuses every decoded mesh and texture.
     */
    leaveMatch(): void {
      loop.stop();
      clearInterval(statusTimer);
      input.dispose();
      hud.hidden = true;
      hud.textContent = '';
      releaseMatchScene([...players, ...officials]);
      ballMesh = null;
      // The underlying geometry is cached and reused by the next match (AssetStore.mesh()),
      // and its constructor reads whatever shape is on it as the new rest pose — so a match
      // that ends mid-celebration, net still bulged, must not leave that behind. See
      // GoalNetting.resetToRest.
      netting?.resetToRest();
      netting = null;
      ballSmoother.clear();
      matchAudio.reset();
      globalThis.__gf = undefined;
      globalThis.__gfTrace = null;
    },
    matchStats() {
      return matchStats.snapshot();
    },
    setCamera(settings: CameraSettings): void {
      matchCamera.settings = { ...settings };
    },
    setMarkers(mode: MarkerMode): void {
      markerMode = mode;
    },
    setDiagnostics(on: boolean): void {
      hud.hidden = !on;
      // Switched off it is left with whatever it last said, which would flash up stale for a
      // quarter of a second the next time it is switched on.
      if (!on) hud.textContent = '';
    },
    /**
     * Put one or both sides into their other shirt, from the visual-options page.
     *
     * Loading the image is asynchronous and a shirt is not worth blocking a frame for, so the
     * players change over whenever it arrives — a frame or two later the first time, and at
     * once after that, since the asset store keeps what it has already decoded.
     */
    setKits(homeKit: number, awayKit: number): void {
      wearKits(homeKit, awayKit).catch((error: unknown) =>
        console.warn('[gf] could not change kit', error),
      );
    },
    /**
     * Light the ground differently, from the visual-options page.
     *
     * A fresh draw from the same distribution the original uses once per match
     * (`Match::SetRandomSunParams`), off a stream of this run's own — so a match played twice
     * and pressed the same number of times is lit the same way both times. Nothing that decides
     * how the picture looks may vary between two runs, or a change to it cannot be measured.
     */
    randomizeSun(): void {
      const next = makeSun(sunStream);
      const light = lights[0];
      if (!light) return;
      simToRender(next.position, light.position);
      light.color = next.color;
      light.radius = next.radius;
    },
    setControllers(controllers): void {
      input.setControllers(
        controllers.filter(
          (controller): controller is typeof controller & { side: -1 | 1 } =>
            controller.side !== 0,
        ),
      );
      // The difficulty handicap follows the device, exactly as `Match::UpdateControllerSetup`
      // (`match.cpp:587`) makes it follow in the original: the side that has just been left to
      // the computer picks the handicap up on its next stat read, and the side that has just
      // been taken over drops it on its next one.
      humanSides.set(controllers);
      // A device that has just left the match may have been ringed; the next frame decides
      // again from scratch, so nothing has to be cleaned up here.
    },
  };
}

declare global {
  // eslint-disable-next-line no-var
  var __gf:
    | {
        loop: FixedLoop;
        stage: Stage;
        ball: Ball;
        match: Match;
        pipeline: DeferredPipeline;
        camera: PerspectiveCamera;
        store: AssetStore;
        lights: DirectionalLight[];
        world: Group;
        players: PlacedHumanoid[];
        officials: PlacedOfficial[];
        input: PlayerInput;
      }
    | undefined;
  /**
   * The fidelity recording in progress, or null when nothing is being recorded.
   *
   * This is how a script gets the bytes: `__gfTrace.toText()` is the whole file as a string,
   * `__gfTrace.stop()` ends the recording early, and `__gfTrace.download()` saves it.
   */
  // eslint-disable-next-line no-var
  var __gfTrace: FidelityTrace | null | undefined;
}

/**
 * The menus, which own the page until a match starts. Null until the boot below runs.
 */
let shell: GameShell | null = null;

// The boot itself, and the only statement in this file with an effect on load. Guarded on
// there being a page at all, so that everything above can be imported and exercised by a test
// runner without a document; in a browser the condition is always true.
if (typeof document !== 'undefined') {
  shell = new GameShell({
    onStartMatch: (fixture) => startMatch(fixture),
    onExit: () => {
      document.body.replaceChildren();
    },
  });
  shell.start();
}
